import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, AlertTriangle, CheckCircle2, MapPin, Ruler, Waves, Plane, Home } from "lucide-react";
import { useLang } from "@/context";
import { T, linkTo, formatPrice, formatMGA, formatSurface } from "@/i18n";
import { ZONES } from "@/data/zones";
import { PropertyCard, PhotoPlaceholder, StatusBadge } from "@/components/PropertyCard";
import { LeadForm } from "@/components/LeadForm";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { WHATSAPP_NUMBER } from "@/config";
import api from "@/api";

export default function PropertyDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = T[lang];
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [currency, setCurrency] = useState("eur");

  useEffect(() => {
    setItem(null); setNotFound(false);
    api.get(`/listings/${slug}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-site py-40 text-center" data-testid="property-not-found">
        <h1 className="h2-serif">{t.notFound.title}</h1>
        <Link to={linkTo(lang, "properties")} className="btn-primary mt-8">{t.common.back}</Link>
      </div>
    );
  }
  if (!item) return <div className="container-site py-40"><div className="h-96 rounded-3xl bg-[#0E4D5C]/5 animate-pulse" /></div>;

  const zone = ZONES.find((z) => z.slug === item.zone);
  const waMsg = t.property.whatsappMsg.replace("{ref}", item.ref_code).replace("{title}", item.title[lang]);
  const facts = [
    { icon: Ruler, label: t.common.landSurface, value: formatSurface(item.surface_m2, lang) },
    item.built_m2 ? { icon: Home, label: t.common.built, value: `${item.built_m2} m²` } : null,
    item.sea_distance_m != null ? { icon: Waves, label: t.common.fromSea, value: item.sea_distance_m === 0 ? "0 m" : `${item.sea_distance_m} m` } : null,
    item.airport_km != null ? { icon: Plane, label: t.common.fromAirport, value: `${item.airport_km} km` } : null,
    zone ? { icon: MapPin, label: t.listings.zoneCol, value: zone.name } : null,
  ].filter(Boolean);

  return (
    <div className="py-28 md:py-36" data-testid="property-detail-page">
      <SEO title={`${item.title[lang]} (${item.ref_code}) — ${t.brand}`} description={item.description[lang].slice(0, 150)} lang={lang} pathKey="property" slug={item.slug} />
      <div className="container-site">
        <Link to={linkTo(lang, "properties")} className="inline-flex items-center gap-2 text-sm text-[#0E4D5C] hover:text-[#B5472B] transition-colors" data-testid="back-to-listings">
          <ArrowLeft size={15} /> {t.nav.properties}
        </Link>

        <div className="mt-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={item.status} />
              {item.demo && <span className="bg-[#E8B64C] text-[#1F2328] text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1" data-testid="detail-demo-badge">{t.common.example} — {t.common.demoData}</span>}
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0E4D5C] mt-4 leading-tight" data-testid="property-title">{item.title[lang]}</h1>
            <p className="mt-2 text-sm text-[#8A8F96] font-medium uppercase tracking-wider" data-testid="property-ref">{item.ref_code} · {t.types[item.type]} · {zone?.name}</p>

            {/* Gallery */}
            <div className="mt-8 grid grid-cols-2 gap-4" data-testid="property-gallery">
              <PhotoPlaceholder className="col-span-2 h-72 sm:h-96 rounded-3xl" />
              <PhotoPlaceholder className="h-40 rounded-2xl" />
              <PhotoPlaceholder className="h-40 rounded-2xl" />
            </div>

            {/* Price */}
            <div className="mt-8 card-site p-6 flex flex-wrap items-center justify-between gap-4" data-testid="price-panel">
              <div>
                <p className="label-site">{t.listings.price}</p>
                {item.price_on_request ? (
                  <p className="font-serif text-3xl text-[#0E4D5C]">{t.common.priceOnRequest}</p>
                ) : (
                  <p className="font-serif text-3xl sm:text-4xl text-[#0E4D5C]" data-testid="property-price">
                    {currency === "eur" ? formatPrice(item.price_eur, lang) : formatMGA(item.price_eur, lang)}
                  </p>
                )}
              </div>
              {!item.price_on_request && (
                <div className="flex rounded-full border border-[#0E4D5C]/20 overflow-hidden">
                  <button onClick={() => setCurrency("eur")} className={`px-4 py-2 text-sm font-medium ${currency === "eur" ? "bg-[#0E4D5C] text-white" : "text-[#0E4D5C]"}`} data-testid="currency-eur">EUR €</button>
                  <button onClick={() => setCurrency("mga")} className={`px-4 py-2 text-sm font-medium ${currency === "mga" ? "bg-[#0E4D5C] text-white" : "text-[#0E4D5C]"}`} data-testid="currency-mga">Ariary Ar</button>
                </div>
              )}
            </div>

            {/* Key facts */}
            <Reveal className="mt-10">
              <h2 className="font-serif text-2xl text-[#1F2328]">{t.property.keyFacts}</h2>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="key-facts">
                {facts.map((f, i) => (
                  <div key={i} className="card-site p-4">
                    <f.icon size={18} className="text-[#B5472B]" />
                    <p className="mt-2 text-xs text-[#8A8F96] uppercase tracking-wider">{f.label}</p>
                    <p className="font-medium text-[#1F2328] mt-0.5">{f.value}</p>
                  </div>
                ))}
              </div>
              {item.services?.[lang]?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {item.services[lang].map((s) => <span key={s} className="rounded-full bg-[#2FA3A0]/10 text-[#0E4D5C] text-xs font-medium px-3 py-1.5">{s}</span>)}
                </div>
              )}
            </Reveal>

            {/* Description */}
            <Reveal className="mt-10">
              <h2 className="font-serif text-2xl text-[#1F2328]">{t.property.description}</h2>
              <div className="mt-4 space-y-4 text-[#1F2328]/80 leading-relaxed" data-testid="property-description">
                {item.description[lang].split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </Reveal>

            {/* Transparency sheet */}
            <Reveal className="mt-10">
              <div className="rounded-2xl bg-[#F6EFE4] border-l-4 border-[#B5472B] p-6 md:p-8 shadow-inner" data-testid="transparency-sheet">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="text-[#B5472B]" size={26} />
                  <h2 className="font-serif text-2xl text-[#0E4D5C]">{t.property.transparency}</h2>
                </div>
                <p className="mt-1 text-xs text-[#2FA3A0] font-semibold uppercase tracking-wider">{t.property.mortgage}</p>
                <dl className="mt-6 space-y-5">
                  <div>
                    <dt className="label-site">{t.property.rightType}</dt>
                    <dd className="text-sm text-[#1F2328]/80 leading-relaxed">{item.right_type[lang]}</dd>
                  </div>
                  <div>
                    <dt className="label-site">{t.property.titleStatus}</dt>
                    <dd className="text-sm text-[#1F2328]/80 leading-relaxed">{item.title_status[lang]}</dd>
                  </div>
                  <div>
                    <dt className="label-site">{t.property.checks}</dt>
                    <dd><ul className="space-y-2 mt-1">
                      {item.checks[lang].map((c) => (
                        <li key={c} className="flex items-start gap-2 text-sm text-[#1F2328]/80"><CheckCircle2 size={15} className="text-[#2FA3A0] mt-0.5 shrink-0" /> {c}</li>
                      ))}
                    </ul></dd>
                  </div>
                  <div>
                    <dt className="label-site !text-[#B5472B]">{t.property.risks}</dt>
                    <dd className="flex items-start gap-2 text-sm text-[#1F2328]/80 leading-relaxed"><AlertTriangle size={15} className="text-[#B5472B] mt-0.5 shrink-0" /> {item.risks[lang]}</dd>
                  </div>
                  <div>
                    <dt className="label-site">{t.property.extraCosts}</dt>
                    <dd className="text-sm text-[#1F2328]/70 leading-relaxed">{t.property.extraCostsText}</dd>
                  </div>
                </dl>
              </div>
            </Reveal>

            {/* Map */}
            <Reveal className="mt-10">
              <h2 className="font-serif text-2xl text-[#1F2328]">{t.property.mapTitle}</h2>
              <div className="mt-4 rounded-3xl bg-[#0E4D5C] text-[#F6EFE4] p-8 flex flex-col sm:flex-row items-center gap-6" data-testid="property-map">
                <MapPin size={40} className="text-[#E8B64C] shrink-0" />
                <div>
                  <p className="font-serif text-xl">{zone?.name}</p>
                  <p className="text-sm text-[#F6EFE4]/70 mt-1">{item.lat}, {item.lng}</p>
                  <p className="text-xs text-[#F6EFE4]/55 mt-2 leading-relaxed">{t.property.approxLocation}</p>
                </div>
              </div>
            </Reveal>

            <p className="mt-8 text-xs text-[#8A8F96] leading-relaxed">{t.footer.disclaimer}</p>
          </div>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 h-fit space-y-5">
            <div className="card-site p-6 md:p-7" data-testid="contact-sidebar">
              <h2 className="font-serif text-xl text-[#0E4D5C]">{t.property.contactTitle}</h2>
              <p className="text-xs text-[#8A8F96] mt-1 mb-5">{t.property.contactSub}</p>
              <LeadForm property={item} testIdPrefix="property-contact-form" />
            </div>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noopener noreferrer"
              className="btn-outline w-full !border-[#2FA3A0] !text-[#2FA3A0] hover:!bg-[#2FA3A0] hover:!text-white" data-testid="property-whatsapp">
              WhatsApp — {item.ref_code}
            </a>
          </aside>
        </div>

        {/* Similar */}
        {item.similar?.length > 0 && (
          <div className="mt-20" data-testid="similar-properties">
            <h2 className="h2-serif !text-3xl">{t.property.similar}</h2>
            <div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {item.similar.map((s) => <PropertyCard key={s.ref_code} item={s} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
